import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { Crown } from "@/components/brand";
import { BRAND } from "@/constants/states";

const HERO_IMG =
  "https://images.unsplash.com/photo-1551836022-4c4c79ecde51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxmZW1hbGUlMjBsYXd5ZXIlMjBjb25zdWx0YXRpb258ZW58MHx8fHwxNzg5MTQxNDEwfDA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "24", label: "States Supported", testId: "hero-stat-states" },
  { value: "10+", label: "Years Dedicated", testId: "hero-stat-experience" },
  { value: "$0", label: "Cost To You", testId: "hero-stat-cost" },
  { value: "1,400+", label: "Recoveries Guided", testId: "hero-stat-recoveries" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const rotateImg = useTransform(scrollYProgress, [0, 1], [0, 5]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[92vh] pt-32 pb-20 flex items-center overflow-hidden bg-[#04111D]"
    >
      <div className="absolute -top-32 -right-32 w-[540px] h-[540px] rounded-full bg-[#0E7C86]/20 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -left-40 w-[480px] h-[480px] rounded-full bg-[#C9A24B]/15 blur-3xl pointer-events-none" />
      <Crown className="absolute right-[8%] top-24 w-40 h-28 opacity-[0.07] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-14 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="hero-eyebrow-badge"
            className="inline-flex items-center gap-2.5 rounded-full border border-[#C9A24B]/35 bg-[#071A2B]/80 px-4 py-2"
          >
            <span className="w-1.5 h-1.5 rotate-45 bg-[#C9A24B]" />
            <span className="font-data text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-[#E8C87A]">
              Women-Founded &bull; Over a Decade of Advocacy &bull; 24 States
            </span>
          </motion.div>

          <h1
            data-testid="hero-heading"
            className="mt-7 font-display font-black tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-[#FBF8F2]"
          >
            <MaskedLine delay={0.25}>When Impact Strikes,</MaskedLine>
            <MaskedLine delay={0.4}>
              We Command <span className="text-[#0E7C86]">Your</span>
            </MaskedLine>
            <MaskedLine delay={0.55}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E8C87A] via-[#C9A24B] to-[#99732B]">
                Recovery.
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            data-testid="hero-tagline-script"
            className="mt-6 font-script text-2xl sm:text-3xl md:text-4xl text-[#E5C378]"
          >
            Trusted connections when they matter most.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            data-testid="hero-description"
            className="mt-5 max-w-xl text-sm sm:text-base text-[#D4DFE6] leading-relaxed"
          >
            Navigating the chaos of an accident shouldn&rsquo;t feel like a solo battle. We connect injured
            drivers and families with elite attorneys, top medical specialists, trusted body shops, and
            immediate rentals &mdash; at zero upfront cost to you.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#04111D] text-xs sm:text-sm uppercase tracking-[0.16em] font-bold px-7 py-4 hover:bg-[#E8C87A] hover:shadow-[0_0_32px_rgba(201,162,75,0.5)] transition-all duration-300"
            >
              Get Matched in 15 Minutes
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href={BRAND.phoneHref}
              data-testid="hero-cta-phone"
              className="inline-flex items-center gap-2 rounded-full border border-[#0E7C86]/60 bg-[#0E7C86]/15 text-[#FBF8F2] text-xs sm:text-sm uppercase tracking-[0.16em] font-semibold px-7 py-4 hover:bg-[#0E7C86]/35 hover:border-[#0E7C86] transition-all duration-300"
            >
              <Phone className="w-4 h-4 text-[#5FC3CC]" />
              Speak to an Advocate
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-[#C9A24B]/15 pt-7"
          >
            {STATS.map((s) => (
              <div key={s.testId} data-testid={s.testId}>
                <p className="font-display font-bold text-2xl sm:text-3xl text-[#E8C87A]">{s.value}</p>
                <p className="mt-1 font-data text-[10px] tracking-[0.18em] uppercase text-[#8EA3B0]">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            style={{ y: yImg, rotate: rotateImg }}
            data-testid="hero-visual-card"
            className="relative"
          >
            <div className="absolute -inset-3 border border-[#C9A24B]/40 translate-x-5 translate-y-5 pointer-events-none" />
            <div className="relative overflow-hidden shadow-[0_32px_80px_rgba(0,0,0,0.55)]">
              <img
                src={HERO_IMG}
                alt="Collision Queens advocate guiding a client through recovery resources"
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#04111D]/85 via-transparent to-[#0E7C86]/10" />
            </div>
            <motion.div
              style={{ y: yBadge }}
              className="absolute -bottom-8 -left-10 backdrop-blur-xl bg-[#071A2B]/90 border border-[#C9A24B]/30 px-6 py-5 shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
            >
              <div className="flex items-center gap-3">
                <Crown className="w-8 h-6 crown-glow" />
                <div>
                  <p className="font-display font-bold text-[#FBF8F2] text-sm tracking-wider">ARIZONA BORN</p>
                  <p className="font-data text-[10px] tracking-[0.28em] uppercase text-[#0E7C86] mt-1">
                    Nationwide Clout
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
